import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SubscriptionService } from '../../services/subscription/subscription.service';
import Swal from 'sweetalert2';
import { Location } from '@angular/common';
@Component({
  selector: 'app-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss']
})
export class PlansComponent implements OnInit {
  isLoggedIn: boolean = false;
  billingCycle: 'monthly' | 'annual' = 'monthly';
  currentPlan: string = 'FREE';
  dynamicPlans: any[] = [];

  constructor(
    private router: Router,
    private subscriptionService: SubscriptionService,
    private location: Location
  ) { }

  ngOnInit(): void {
    this.isLoggedIn = !!localStorage.getItem('sessionToken');
    this.currentPlan = this.subscriptionService.getCurrentPlanName().toUpperCase();
    this.subscriptionService.currentSubscription$.subscribe(sub => {
      if (sub && sub.current_plan) {
        this.currentPlan = sub.current_plan.toUpperCase();
      }
    });
    this.subscriptionService.getPlans().subscribe(res => {
      if (res && res.status === 'success' && res.data) {
        this.dynamicPlans = res.data.filter((p: any) => p.plan_code !== 'PLATINUM' && p.plan_code !== 'PREMIUM PLUS');
      }
    });
  }
  goBack(): void {
  this.location.back();
}

  toggleBilling(): void {
    this.billingCycle = this.billingCycle === 'monthly' ? 'annual' : 'monthly';
  }

  setBilling(cycle: 'monthly' | 'annual'): void {
    this.billingCycle = cycle;
  }

  getPrice(plan: any): number {
    if (!plan || !plan.pricing) return 0;
    const item = plan.pricing.find((p: any) => p.cycle === this.billingCycle) || plan.pricing[0];
    return item ? item.price : 0;
  }

  getCycleLabel(plan: any): string {
    if (plan.plan_code === 'FREE') return '/ forever';
    return this.billingCycle === 'annual' ? '/ year' : '/ month';
  }

  getPlanIcon(planCode: string): string {
    switch (planCode) {
      case 'FREE': return 'person_search';
      case 'SILVER': return 'favorite';
      case 'GOLD': return 'diamond';
      case 'EXPRESS': return 'stars';
      case 'PLATINUM': return 'workspace_premium';
      default: return 'stars';
    }
  }

  private getPlanFeatureValue(plan: any, path: string, fallback: any): any {
    const features = plan?.features || {};
    const value = path.split('.').reduce((acc: any, key: string) => {
      if (acc && typeof acc === 'object' && key in acc) {
        return acc[key];
      }
      return undefined;
    }, features);

    return value ?? fallback;
  }

  private formatLimitValue(value: any, fallback: number, isUnlimited = false): string {
    const numericValue = Number(value ?? fallback ?? 0);
    if (isUnlimited || numericValue > 1000) {
      return 'Unlimited';
    }
    return `${numericValue}`;
  }

  getPlanSections(plan: any): { category: string, items: { text: string, available: boolean }[] }[] {
    if (!plan) return [];
    const code = (plan.plan_code || '').toUpperCase();
    const limits = plan?.features?.limits || {};
    const communication = plan?.features?.communication || {};
    const discovery = plan?.features?.discovery || {};
    const premium = plan?.features?.premium || {};

    const dailyProfileLimit = this.formatLimitValue(limits.dailyProfileLimit, 20, Boolean(discovery.unlimitedSearch));
    const monthlyInterestLimit = this.formatLimitValue(limits.monthlyInterestLimit, 10, Boolean(premium.unlimitedInterests));
    const favoritesLimit = this.formatLimitValue(limits.favoritesLimit, 5);
    const savedFiltersLimit = this.formatLimitValue(limits.savedFilters, 0);
    const boostMonthlyLimit = this.formatLimitValue(limits.boostMonthlyLimit, 0);
    const viewAnnualEarningsEnabled = Boolean(communication.viewAnnualEarnings);
    const galleryViewEnabled = Boolean(discovery.galleryView);
    const chatAccessEnabled = Boolean(communication.chatAccess);
    const contactViewEnabled = Boolean(communication.contactView);
    const profileBoostEnabled = Boolean(premium.profileBoost);
    const featuredProfileEnabled = Boolean(premium.featuredProfile);
    const horoscopeDownloadEnabled = Boolean(premium.horoscopeDownload);
    const whoViewedMyProfileEnabled = Boolean(discovery.whoViewedMyProfile);

    if (code === 'FREE') {
      return [
        {
          category: 'SEARCH & DISCOVERY',
          items: [
            { text: `Daily Profile Discovery: ${dailyProfileLimit} views / day`, available: true },
            { text: `View Candidate Annual Earnings: ${viewAnnualEarningsEnabled ? 'Unlocked' : 'Locked (Upgrade to see)'}`, available: viewAnnualEarningsEnabled },
            { text: `Full Gallery Photo Access: ${galleryViewEnabled ? 'Included (Unblurred)' : 'Restricted'}`, available: galleryViewEnabled }
          ]
        },
        {
          category: 'INTERESTS & CONNECTING',
          items: [
            { text: `Send Express Interests: ${monthlyInterestLimit} requests / mo`, available: true },
            { text: 'Accept / Decline Incoming Requests: Included', available: true },
            { text: `Instant Real-time Chat Messaging: ${chatAccessEnabled ? 'Unlocked' : 'Locked'}`, available: chatAccessEnabled },
            { text: `Chat with Free Members: ${chatAccessEnabled ? 'Yes' : 'No'}`, available: chatAccessEnabled }
          ]
        },
        {
          category: 'SAVED PRESETS & FAVORITES',
          items: [
            { text: `Shortlist & Save Favorites: Up to ${favoritesLimit} profiles`, available: true },
            { text: `Save Custom Search Presets: ${savedFiltersLimit} saved filters`, available: Number(limits.savedFilters || 0) > 0 }
          ]
        },
        {
          category: 'VERIFIED CONTACTS & PRIVACY',
          items: [
            { text: `Direct Phone & Email Access: ${contactViewEnabled ? 'Unlocked' : 'Locked always'}`, available: contactViewEnabled }
          ]
        },
        {
          category: 'PREMIUM VIP & ADVANCED TOOLS',
          items: [
            { text: `24h Profile Boost: ${profileBoostEnabled ? `${boostMonthlyLimit} Boosts / mo Included` : 'Locked (Upgrade to Gold)'}`, available: profileBoostEnabled },
            { text: `Featured VIP Badge Recognition: ${featuredProfileEnabled ? 'Included' : 'No'}`, available: featuredProfileEnabled },
            { text: `Astrological Horoscope PDF Downloads: ${horoscopeDownloadEnabled ? 'Included' : 'No'}`, available: horoscopeDownloadEnabled },
            { text: `See who viewed your profile: ${whoViewedMyProfileEnabled ? 'Included' : 'No'}`, available: whoViewedMyProfileEnabled }
          ]
        }
      ];
    } else if (code === 'SILVER') {
      return [
        {
          category: 'SEARCH & DISCOVERY',
          items: [
            { text: `Daily Profile Discovery: ${dailyProfileLimit} views / day`, available: true },
            { text: `View Candidate Annual Earnings: ${viewAnnualEarningsEnabled ? 'Unlocked' : 'Locked'}`, available: viewAnnualEarningsEnabled },
            { text: `Full Gallery Photo Access: ${galleryViewEnabled ? 'Included (Unblurred)' : 'Restricted'}`, available: galleryViewEnabled }
          ]
        },
        {
          category: 'INTERESTS & CONNECTING',
          items: [
            { text: `Send Express Interests: ${monthlyInterestLimit} requests / mo`, available: true },
            { text: 'Accept / Decline Incoming Requests: Included', available: true },
            { text: `Instant Real-time Chat Messaging: ${chatAccessEnabled ? 'Unlocked' : 'Locked'}`, available: chatAccessEnabled },
            { text: `Chat with Free Members: ${chatAccessEnabled ? 'Yes' : 'No'}`, available: chatAccessEnabled }
          ]
        },
        {
          category: 'SAVED PRESETS & FAVORITES',
          items: [
            { text: `Shortlist & Save Favorites: Up to ${favoritesLimit} profiles`, available: true },
            { text: `Save Custom Search Presets: Up to ${savedFiltersLimit} presets`, available: true }
          ]
        },
        {
          category: 'VERIFIED CONTACTS & PRIVACY',
          items: [
            { text: `Direct Phone & Email Access: ${contactViewEnabled ? 'Unlocked' : 'Locked always'}`, available: contactViewEnabled }
          ]
        },
        {
          category: 'PREMIUM VIP & ADVANCED TOOLS',
          items: [
            { text: `24h Profile Boost: ${profileBoostEnabled ? `${boostMonthlyLimit} Boosts / mo Included` : 'Locked (Upgrade to Gold)'}`, available: profileBoostEnabled },
            { text: `Featured VIP Badge Recognition: ${featuredProfileEnabled ? 'Included' : 'No'}`, available: featuredProfileEnabled },
            { text: `Astrological Horoscope PDF Downloads: ${horoscopeDownloadEnabled ? 'Included' : 'No'}`, available: horoscopeDownloadEnabled },
            { text: `See who viewed your profile: ${whoViewedMyProfileEnabled ? 'Included' : 'No'}`, available: whoViewedMyProfileEnabled }
          ]
        }
      ];
    } else if (code === 'GOLD') {
      return [
        {
          category: 'SEARCH & DISCOVERY',
          items: [
            { text: `Daily Profile Discovery: ${dailyProfileLimit} views / day`, available: true },
            { text: `View Candidate Annual Earnings: ${viewAnnualEarningsEnabled ? 'Unlocked' : 'Locked'}`, available: viewAnnualEarningsEnabled },
            { text: `Full Gallery Photo Access: ${galleryViewEnabled ? 'Included (Unblurred)' : 'Restricted'}`, available: galleryViewEnabled }
          ]
        },
        {
          category: 'INTERESTS & CONNECTING',
          items: [
            { text: `Send Express Interests: ${monthlyInterestLimit}`, available: true },
            { text: 'Accept / Decline Incoming Requests: Included', available: true },
            { text: `Instant Real-time Chat Messaging: ${chatAccessEnabled ? 'Unlocked' : 'Locked'}`, available: chatAccessEnabled },
            { text: `Chat with Free Members: ${chatAccessEnabled ? 'Yes' : 'No'}`, available: chatAccessEnabled }
          ]
        },
        {
          category: 'SAVED PRESETS & FAVORITES',
          items: [
            { text: `Shortlist & Save Favorites: Up to ${favoritesLimit} profiles`, available: true },
            { text: `Save Custom Search Presets: Up to ${savedFiltersLimit} presets`, available: true }
          ]
        },
        {
          category: 'VERIFIED CONTACTS & PRIVACY',
          items: [
            { text: `Direct Phone & Email Access: ${contactViewEnabled ? 'Unlocked (On Acceptance)' : 'Locked'}`, available: contactViewEnabled }
          ]
        },
        {
          category: 'PREMIUM VIP & ADVANCED TOOLS',
          items: [
            { text: `24h Profile Boost: ${boostMonthlyLimit} Boosts / mo Included`, available: profileBoostEnabled },
            { text: `Featured VIP Badge Recognition: ${featuredProfileEnabled ? 'Included' : 'No'}`, available: featuredProfileEnabled },
            { text: `Astrological Horoscope PDF Downloads: ${horoscopeDownloadEnabled ? 'Included' : 'No'}`, available: horoscopeDownloadEnabled },
            { text: `See who viewed your profile: ${whoViewedMyProfileEnabled ? 'Included' : 'No'}`, available: whoViewedMyProfileEnabled }
          ]
        }
      ];
    } else if (code === 'EXPRESS') {
      return [
        {
          category: 'DEDICATED CONCIERGE (RM) & MATCHMAKING',
          items: [
            { text: 'Dedicated Personal Relationship Manager (RM)', available: true },
            { text: 'White-Glove Handpicked Profiles & Match Reviews', available: true },
            { text: 'Private 3-Panel Concierge Workspace & Chat', available: true },
            { text: 'Direct RM Negotiation & Family Coordination', available: true }
          ]
        },
        {
          category: 'UNLIMITED SEARCH & DIRECT CONTACTS',
          items: [
            { text: `Daily Profile Discovery: ${dailyProfileLimit} views / day`, available: true },
            { text: `Direct Phone & Email Access: On Acceptance`, available: contactViewEnabled },
            { text: `Full Gallery Photo Access: ${galleryViewEnabled ? 'Included (Unblurred)' : 'Restricted'}`, available: galleryViewEnabled },
            { text: `View Candidate Annual Earnings: ${viewAnnualEarningsEnabled ? 'Unlocked' : 'Locked'}`, available: viewAnnualEarningsEnabled }
          ]
        },
        {
          category: 'PREMIUM VIP & ADVANCED TOOLS',
          items: [
            { text: 'Top Priority VIP Placement in Search Results', available: true },
            { text: `Astrological Horoscope PDF Downloads: ${horoscopeDownloadEnabled ? 'Included' : 'No'}`, available: horoscopeDownloadEnabled },
            { text: `See who viewed your profile: ${whoViewedMyProfileEnabled ? 'Included' : 'No'}`, available: whoViewedMyProfileEnabled },
            { text: `Profile Boosts: ${boostMonthlyLimit} / mo Included`, available: profileBoostEnabled }
          ]
        }
      ];
    } else if (code === 'PLATINUM') {
      return [
        {
          category: 'EVERYTHING IN GOLD, PLUS:',
          items: [
            { text: `24h Profile Boost: ${boostMonthlyLimit} Boosts / mo Included`, available: true },
            { text: `Daily Profile Discovery: ${dailyProfileLimit} views / day`, available: true },
            { text: 'Dedicated Relationship Assistant Support', available: true },
            { text: 'Top Priority Search Placement & Ranking', available: true },
            { text: 'White-Glove VIP Matchmaking Service', available: true }
          ]
        }
      ];
    }

    return [
      {
        category: 'FEATURES INCLUDED',
        items: (plan.ui_feature_list || []).map((feat: string) => ({ text: feat, available: true }))
      }
    ];
  }

  isPlanCurrent(planCode: string): boolean {
    const norm = planCode.toUpperCase();
    return norm === this.currentPlan || (this.currentPlan === 'GOLD' && norm === 'PREMIUM') || (this.currentPlan === 'PREMIUM' && norm === 'GOLD') || (this.currentPlan === 'PLATINUM' && norm === 'PREMIUM PLUS') || (this.currentPlan === 'PREMIUM PLUS' && norm === 'PLATINUM');
  }

  async selectPlan(planName: string, amount: number = 0): Promise<void> {
    const normPlan = planName.toUpperCase();
    if (this.isPlanCurrent(planName)) {
      Swal.fire({
        title: `Active Membership`,
        text: `You are already subscribed to the ${planName} plan!`,
        icon: 'info',
        confirmButtonColor: '#800020'
      });
      return;
    }

    const profileId = localStorage.getItem('profileId') || '661fd44ab323b56a09518de3';

    Swal.fire({
      title: `Upgrade to ${planName}?`,
      text: `You are initiating a secure subscription checkout for ${planName}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Proceed to Razorpay',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#800020'
    }).then(async result => {
      if (!result.isConfirmed) return;

      Swal.fire({
        title: 'Initializing Checkout...',
        text: 'Connecting to Razorpay secure gateway...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });

      await this.subscriptionService.loadRazorpaySDK();

      this.subscriptionService.createOrder(planName, this.billingCycle, profileId).subscribe({
        next: (orderRes) => {
          Swal.close();
          if (!orderRes || orderRes.status !== 'success') {
            Swal.fire('Checkout Error', orderRes?.message || 'Could not initiate transaction', 'error');
            return;
          }

          const orderData = orderRes.data;
          const options = {
            key: orderData.key_id || 'rzp_test_1234567890',
            amount: orderData.amount,
            currency: orderData.currency || 'INR',
            name: 'Weddingly VIP',
            description: `${planName} Membership Upgrade`,
            order_id: orderData.order_id,
            handler: (response: any) => {
              Swal.fire({
                title: 'Verifying Signature...',
                text: 'Cryptographic validation in progress...',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
              });

              this.subscriptionService.verifySignature({
                razorpay_order_id: response.razorpay_order_id || orderData.order_id,
                razorpay_payment_id: response.razorpay_payment_id || `pay_rzp_${Date.now()}`,
                razorpay_signature: response.razorpay_signature || 'test_signature_bypass',
                profileId
              }).subscribe({
                next: (verifyRes) => {
                  if (verifyRes && verifyRes.status === 'success') {
                    this.currentPlan = normPlan;
                    this.subscriptionService.fetchCurrentSubscription(profileId).subscribe();
                    Swal.fire({
                      title: '🎉 Upgrade Successful!',
                      text: `Your ${planName} membership is now officially ACTIVE.`,
                      icon: 'success',
                      confirmButtonColor: '#800020'
                    });
                  } else {
                    Swal.fire('Verification Failed', 'Transaction signature mismatch.', 'error');
                  }
                },
                error: (vErr) => {
                  Swal.fire('Error', vErr.error?.message || 'Verification service error', 'error');
                }
              });
            },
            prefill: {
              name: 'John Seeker',
              email: 'john@gmail.com',
              contact: '9876543210'
            },
            theme: {
              color: '#800020'
            },
            modal: {
              ondismiss: () => {
                Swal.fire('Checkout Cancelled', 'You closed the payment popup.', 'info');
              }
            }
          };

          if ((window as any).Razorpay) {
            const rzp = new (window as any).Razorpay(options);
            rzp.open();
          } else {
            // Simulated fallback if browser blocked external Razorpay JS loading
            options.handler({
              razorpay_order_id: orderData.order_id,
              razorpay_payment_id: `pay_rzp_sim_${Date.now()}`,
              razorpay_signature: 'simulated_test_sig'
            });
          }
        },
        error: (err) => {
          Swal.fire('Order Creation Failed', err.error?.message || 'Server error communicating with payment gateway.', 'error');
        }
      });
    });
  }
}
